import {
  DirectionsBoatOutlined,
  EditOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  FiberManualRecordOutlined,
  HistoryOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { styled } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import getVoyageStatusChip from '../voyage/VoyageStatusUtils';
import { StockBreakdownPopover } from '../../../../../../features/stock';
import { formatNumberDE } from '../../../../../../shared/utils/formatNumber';
import {
  getScenarioHighlightColor,
  scenarioCreatedBgColor,
  scenarioDeltaTextSx,
} from '../../../../../../shared/utils/scenarioHighlight';
import useScenarioMode from '../../../../../../shared/hooks/useScenarioMode';
import { VesselCapacityValue } from '../shared';
import {
  calculateSum,
  withVesselCapacity,
} from '../../../../utils/vesselCapacityUtils';

// ── Utility Functions ────────────────────────────────────────────────

const formatDateCompact = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date)
    ? 'N/A'
    : new Intl.DateTimeFormat('en-DE', {
        day: 'numeric',
        month: 'short',
      }).format(date);
};

const groupShipmentsByCountry = (
  shipments,
  shipmentDeltaFields = {},
  shipmentChangeTypes = {}
) => {
  const map = new Map();
  for (const s of shipments) {
    const countryCode = s.market?.country?.countryCode ?? 'UNKNOWN';
    if (!map.has(countryCode)) {
      map.set(countryCode, {
        key: countryCode,
        label: s.market?.country?.name ?? 'Unknown',
        firstShippingLineOffer: 0,
        adjustedPORequest: 0,
        finalAgreement: 0,
        effectiveShipped: 0,
        readyForShipment: null,
        totalBlockedCars: null,
        hasStock: false,
        stockBreakdown: null,
        _changedFields: new Set(),
      });
    }
    const row = map.get(countryCode);
    row.firstShippingLineOffer += s.firstShippingLineOffer ?? 0;
    row.adjustedPORequest += s.adjustedPORequest ?? 0;
    row.finalAgreement += s.finalAgreement ?? 0;
    row.effectiveShipped += s.effectiveShipped ?? 0;
    if (s.stock != null) {
      row.hasStock = true;
      row.readyForShipment =
        (row.readyForShipment ?? 0) + (s.stock.readyForShipment ?? 0);
      row.totalBlockedCars =
        (row.totalBlockedCars ?? 0) + (s.stock.totalBlockedCars ?? 0);
    }
    if (s.stockBreakdown) row.stockBreakdown = s.stockBreakdown;
    // Track scenario delta changes per country group
    const sid = s.id ? String(s.id) : null;
    if (sid) {
      const dFields = shipmentDeltaFields[sid];
      if (dFields) {
        for (const f of dFields) row._changedFields.add(f);
      }
      if (shipmentChangeTypes[sid] === 'CREATED') {
        row._changedFields.add('market');
        row._changedFields.add('firstShippingLineOffer');
        row._changedFields.add('adjustedPORequest');
        row._changedFields.add('finalAgreement');
        row._changedFields.add('effectiveShipped');
      }
    }
  }
  return [...map.values()];
};

/**
 * Calculates what percentage of the segment between two dates has elapsed.
 * Returns 0 if before start, 100 if after end, or the proportional value.
 */
const segmentProgress = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start) || isNaN(end)) return null;
  if (now <= start) return 0;
  if (now >= end) return 100;
  return ((now - start) / (end - start)) * 100;
};

// ── Scenario Delta Helpers ───────────────────────────────────────────

const getDeltaSx = (theme) => scenarioDeltaTextSx(theme);

const hasAnyVoyageDelta = (destinations, fields, deltas) => {
  if (!deltas?.voyageDeltaFields) return false;
  return Object.values(destinations).some((dest) =>
    dest.voyages.some((v) => {
      const vid = v.id ? String(v.id) : null;
      if (!vid) return false;
      if (deltas.voyageChangeTypes?.[vid] === 'CREATED') return true;
      const df = deltas.voyageDeltaFields[vid];
      return df && fields.some((f) => df.has(f));
    })
  );
};

const hasDestVoyageDelta = (voyages, fields, deltas) => {
  if (!deltas?.voyageDeltaFields) return false;
  return voyages.some((v) => {
    const vid = v.id ? String(v.id) : null;
    if (!vid) return false;
    if (deltas.voyageChangeTypes?.[vid] === 'CREATED') return true;
    const df = deltas.voyageDeltaFields[vid];
    return df && fields.some((f) => df.has(f));
  });
};

const hasDestShipmentDelta = (shipments, field, deltas) => {
  if (!deltas?.shipmentDeltaFields) return false;
  return (shipments ?? []).some((s) => {
    const sid = s.id ? String(s.id) : null;
    if (!sid) return false;
    if (deltas.shipmentChangeTypes?.[sid] === 'CREATED') return true;
    const df = deltas.shipmentDeltaFields[sid];
    return df && df.has(field);
  });
};

// ── Styled Components ────────────────────────────────────────────────

const BorderedGrid = styled(Grid)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  paddingTop: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

// ── Route Line Visualization ─────────────────────────────────────────

const RouteLine = ({ departurePort, etdDate, destinations }) => {
  const theme = useTheme();

  // Build ordered stops: departure, then each destination sorted by ETA
  // Exclude destinations where all voyages are cancelled
  const destEntries = Object.values(destinations);
  const sortedDests = [...destEntries].sort(
    (a, b) => new Date(a.actualETA ?? 0) - new Date(b.actualETA ?? 0)
  );

  // Build segments: departure → dest1 → dest2 → ...
  const stops = [
    {
      label: departurePort?.unLocode ?? 'N/A',
      name: departurePort?.name ?? '',
      country: departurePort?.country?.name ?? '',
      date: etdDate,
      isDeparture: true,
    },
    ...sortedDests.map((d) => ({
      label: d.port?.unLocode ?? 'N/A',
      name: d.port?.name ?? '',
      country: d.port?.country?.name ?? '',
      date: d.actualETA,
      isDeparture: false,
    })),
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 2,
        px: 1,
        overflow: 'hidden',
      }}
    >
      {stops.map((stop, idx) => {
        const isLast = idx === stops.length - 1;

        // Calculate segment progress (from previous stop to this one)
        let progress = null;
        if (idx > 0) {
          const prevDate = stops[idx - 1].date;
          progress = segmentProgress(prevDate, stop.date);
        }

        return (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: isLast ? '0 0 auto' : 1,
              minWidth: 0,
            }}
          >
            {/* Port dot + label */}
            <Tooltip
              title={
                [stop.name, stop.country].filter(Boolean).join(', ') ||
                stop.label
              }
              arrow
              placement='top'
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flexShrink: 0,
                  minWidth: 60,
                  cursor: 'pointer',
                }}
              >
                <FiberManualRecordOutlined
                  sx={{
                    fontSize: idx === 0 ? 18 : 14,
                    color:
                      idx === 0
                        ? 'primary.main'
                        : progress !== null && progress >= 100
                          ? 'success.main'
                          : 'action.disabled',
                  }}
                />
                <Typography
                  variant='body1'
                  sx={{
                    fontWeight: 600,
                    mt: 0.3,
                    color:
                      idx === 0
                        ? 'primary.main'
                        : progress !== null && progress >= 100
                          ? 'success.main'
                          : 'text.primary',
                  }}
                >
                  {stop.label}
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {stop.isDeparture ? 'ETD' : 'ETA'}{' '}
                  {formatDateCompact(stop.date)}
                </Typography>
              </Box>
            </Tooltip>

            {/* Connecting line to next stop */}
            {!isLast &&
              (() => {
                const nextDate = stops[idx + 1].date;
                const prog = segmentProgress(stop.date, nextDate);
                const pct = prog != null ? Math.max(0, Math.min(100, prog)) : 0;

                return (
                  <Box
                    sx={{
                      flex: 1,
                      mx: 0.5,
                      position: 'relative',
                      height: 4,
                      minWidth: 20,
                    }}
                  >
                    {/* Background track */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: 2,
                        backgroundColor: theme.palette.grey[300],
                      }}
                    />
                    {/* Filled progress */}
                    {pct > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          bottom: 0,
                          width: `${pct}%`,
                          borderRadius: 2,
                          backgroundColor:
                            pct >= 100 ? 'success.main' : 'primary.main',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    )}
                  </Box>
                );
              })()}
          </Box>
        );
      })}
    </Box>
  );
};

// ── Destination Port Section ─────────────────────────────────────────

const DestinationPortSection = ({
  destinationPort,
  voyages,
  actualETA,
  etdDate,
  handleHistoryClick,
  scenarioDeltas,
  baseDest,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const activeVoyages = useMemo(
    () => voyages.filter((v) => !v.cancelled),
    [voyages]
  );

  const allShipments = useMemo(
    () => activeVoyages.flatMap((v) => v.shipments ?? []),
    [activeVoyages]
  );

  const countryRows = useMemo(
    () =>
      groupShipmentsByCountry(
        allShipments,
        scenarioDeltas?.shipmentDeltaFields,
        scenarioDeltas?.shipmentChangeTypes
      ),
    [
      allShipments,
      scenarioDeltas?.shipmentDeltaFields,
      scenarioDeltas?.shipmentChangeTypes,
    ]
  );

  // Base (non-scenario) shipments for value comparison
  const baseAllShipments = useMemo(
    () =>
      baseDest
        ? baseDest.voyages
            .filter((v) => !v.cancelled)
            .flatMap((v) => v.shipments ?? [])
        : null,
    [baseDest]
  );

  // The destination row no longer displays First Shipping Line Offer —
  // per the latest acceptance criteria it is shown only once, under the
  // "First Shipping Line Offer" column header in the ship-wide grand totals
  // (see `ShipCard`'s `grandTotals`), resolved across all destinations.
  const sums = useMemo(() => {
    return {
      adjustedPORequest: calculateSum(allShipments, 'adjustedPORequest'),
      finalAgreement: calculateSum(allShipments, 'finalAgreement'),
      effectiveShipped: calculateSum(allShipments, 'effectiveShipped'),
      readyForShipment: calculateSum(allShipments, 'readyForShipment', true),
      totalBlockedCars: calculateSum(allShipments, 'totalBlockedCars', true),
    };
  }, [allShipments]);

  const baseSums = useMemo(() => {
    if (!baseAllShipments) return null;
    return {
      adjustedPORequest: calculateSum(baseAllShipments, 'adjustedPORequest'),
      finalAgreement: calculateSum(baseAllShipments, 'finalAgreement'),
      effectiveShipped: calculateSum(baseAllShipments, 'effectiveShipped'),
    };
  }, [baseAllShipments]);

  // Value-comparison: override _changedFields per country row when base available
  const effectiveCountryRows = useMemo(() => {
    if (!baseDest || !baseAllShipments) return countryRows;
    const baseRows = groupShipmentsByCountry(baseAllShipments);
    const baseMap = new Map();
    for (const br of baseRows) baseMap.set(br.key, br);
    return countryRows.map((row) => {
      const base = baseMap.get(row.key);
      const changed = new Set();
      if (!base) {
        changed.add('market');
        changed.add('firstShippingLineOffer');
        changed.add('adjustedPORequest');
        changed.add('finalAgreement');
        changed.add('effectiveShipped');
      } else {
        if (row.label !== base.label) changed.add('market');
        if (row.firstShippingLineOffer !== base.firstShippingLineOffer)
          changed.add('firstShippingLineOffer');
        if (row.adjustedPORequest !== base.adjustedPORequest)
          changed.add('adjustedPORequest');
        if (row.finalAgreement !== base.finalAgreement)
          changed.add('finalAgreement');
        if (row.effectiveShipped !== base.effectiveShipped)
          changed.add('effectiveShipped');
      }
      return { ...row, _changedFields: changed };
    });
  }, [countryRows, baseDest, baseAllShipments]);

  const isPortChanged = baseDest
    ? (destinationPort?.unLocode ?? '') !== (baseDest.port?.unLocode ?? '')
    : hasDestVoyageDelta(
        voyages,
        ['destinationPort', 'destinationCountry'],
        scenarioDeltas
      );
  const isEtaChanged = baseDest
    ? actualETA !== baseDest.actualETA
    : hasDestVoyageDelta(voyages, ['actualETA', 'initialETA'], scenarioDeltas);
  const isDestCreated = voyages.some(
    (v) =>
      v.id && scenarioDeltas?.voyageChangeTypes?.[String(v.id)] === 'CREATED'
  );
  const sumDelta = (field) => {
    if (baseSums) {
      return sums[field] !== baseSums[field];
    }
    return (
      isDestCreated || hasDestShipmentDelta(allShipments, field, scenarioDeltas)
    );
  };

  const hasAnyStock = useMemo(
    () => allShipments.some((s) => s.stock != null),
    [allShipments]
  );

  const allCancelled = useMemo(
    () => voyages.length > 0 && voyages.every((v) => v.cancelled === true),
    [voyages]
  );

  const isHistoryDisabled =
    !voyages.some((v) => v.changed) && !allShipments.some((s) => s.changed);

  const targetVoyage = useMemo(
    () =>
      [...voyages].sort(
        (a, b) => new Date(a.actualETA ?? 0) - new Date(b.actualETA ?? 0)
      )[0],
    [voyages]
  );

  return (
    <Box
      sx={{
        py: 0.75,
        '&:not(:last-child)': {
          borderBottom: `1px dashed ${theme.palette.divider}`,
        },
      }}
    >
      <Grid container spacing={0.5} columns={19} sx={{ alignItems: 'center' }}>
        <Grid xs={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size='small'
              onClick={() => setExpanded(!expanded)}
              sx={{ p: 0.25 }}
            >
              {expanded ? (
                <ExpandLessOutlined fontSize='small' />
              ) : (
                <ExpandMoreOutlined fontSize='small' />
              )}
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant='body1'
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.3,
                  ...(isPortChanged && {
                    color: getScenarioHighlightColor(theme),
                  }),
                }}
              >
                {destinationPort?.unLocode ?? 'N/A'}
                <Typography
                  component='span'
                  variant='body1'
                  sx={{
                    ...(isPortChanged
                      ? { color: getScenarioHighlightColor(theme) }
                      : { color: 'text.secondary' }),
                    ml: 0.5,
                  }}
                >
                  {destinationPort?.name ?? ''}
                </Typography>
              </Typography>
              <Typography
                variant='body1'
                sx={{
                  color: isEtaChanged
                    ? getScenarioHighlightColor(theme)
                    : 'text.secondary',
                  lineHeight: 1,
                  display: 'block',
                }}
              >
                ETA {formatDateCompact(actualETA)} ·{' '}
                {effectiveCountryRows.length} market
                {effectiveCountryRows.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box
              sx={{
                transform: 'scale(0.8)',
                transformOrigin: 'left center',
                flexShrink: 0,
              }}
            >
              <Tooltip title='View History'>
                <span>
                  <IconButton
                    aria-label='View History'
                    color={!isHistoryDisabled ? 'primary' : 'default'}
                    disabled={isHistoryDisabled}
                    onClick={() => handleHistoryClick?.(targetVoyage)}
                    size='small'
                  >
                    <HistoryOutlined fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Grid>
        <Grid xs={3} />
        <Grid xs={3}>
          <Typography
            variant='body1'
            sx={sumDelta('adjustedPORequest') ? getDeltaSx(theme) : undefined}
          >
            {formatNumberDE(sums.adjustedPORequest)}
          </Typography>
        </Grid>
        <Grid xs={3}>
          <Typography
            variant='body1'
            sx={sumDelta('finalAgreement') ? getDeltaSx(theme) : undefined}
          >
            {formatNumberDE(sums.finalAgreement)}
          </Typography>
        </Grid>
        <Grid xs={3}>
          <Typography
            variant='body1'
            sx={sumDelta('effectiveShipped') ? getDeltaSx(theme) : undefined}
          >
            {formatNumberDE(sums.effectiveShipped)}
          </Typography>
        </Grid>
        <Grid
          xs={2}
          paddingLeft={2}
          sx={{ borderLeft: `1px solid ${theme.palette.divider}` }}
        >
          <Typography variant='body1'>
            {hasAnyStock ? formatNumberDE(sums.readyForShipment) : '-'}
          </Typography>
        </Grid>
        <Grid
          xs={2}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='body1'>
            {hasAnyStock ? formatNumberDE(sums.totalBlockedCars) : '-'}
          </Typography>
          <Box
            sx={{
              transform: 'scale(0.8)',
              transformOrigin: 'right center',
              flexShrink: 0,
            }}
          >
            {getVoyageStatusChip(
              etdDate,
              actualETA,
              allCancelled,
              sums.effectiveShipped
            )}
          </Box>
        </Grid>

        {/* Expanded: per-market rows aggregated by country */}
        {expanded &&
          effectiveCountryRows.map((row) => (
            <MarketRow
              key={row.key}
              row={row}
              scenarioDeltas={scenarioDeltas}
            />
          ))}
      </Grid>
    </Box>
  );
};

const MarketRow = ({ row }) => {
  const theme = useTheme();
  const ch = row._changedFields;
  const d = (field) => ch && ch.has(field);
  return (
    <>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
            color: d('market')
              ? getScenarioHighlightColor(theme)
              : 'text.secondary',
          }}
        >
          {row.label}
        </Typography>
      </Grid>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={{
            color: d('firstShippingLineOffer')
              ? getScenarioHighlightColor(theme)
              : 'text.secondary',
          }}
        >
          {formatNumberDE(row.firstShippingLineOffer)}
        </Typography>
      </Grid>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={{
            color: d('adjustedPORequest')
              ? getScenarioHighlightColor(theme)
              : 'text.secondary',
          }}
        >
          {formatNumberDE(row.adjustedPORequest)}
        </Typography>
      </Grid>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={{
            color: d('finalAgreement')
              ? getScenarioHighlightColor(theme)
              : 'text.secondary',
          }}
        >
          {formatNumberDE(row.finalAgreement)}
        </Typography>
      </Grid>
      <Grid xs={3}>
        <Typography
          variant='body1'
          sx={{
            color: d('effectiveShipped')
              ? getScenarioHighlightColor(theme)
              : 'text.secondary',
          }}
        >
          {formatNumberDE(row.effectiveShipped)}
        </Typography>
      </Grid>
      <Grid
        xs={2}
        paddingLeft={2}
        sx={{ borderLeft: `1px solid ${theme.palette.divider}` }}
      >
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {row.hasStock && row.readyForShipment != null
            ? formatNumberDE(row.readyForShipment)
            : '-'}
        </Typography>
      </Grid>
      <Grid xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {row.hasStock && row.totalBlockedCars != null
            ? formatNumberDE(row.totalBlockedCars)
            : '-'}
        </Typography>
        <StockBreakdownPopover stockBreakdown={row.stockBreakdown} />
      </Grid>
    </>
  );
};

// ── Main Component ───────────────────────────────────────────────────

const ShipCard = ({
  shipGroup,
  baseShipGroup,
  CardHeader,
  CardContainer,
  handleHistoryClick,
  onEditGroup,
  scenarioDeltas,
}) => {
  const { isScenarioViewOnly, isScenarioActive } = useScenarioMode();
  const theme = useTheme();

  const { ship, etdDate, departurePort, portOperator, carrier, destinations } =
    shipGroup;

  // Detect if this entire group is scenario-created (no base data + all voyages CREATED)
  const isGroupScenarioCreated = useMemo(() => {
    if (!isScenarioActive || baseShipGroup) return false;
    return Object.values(destinations).some((dest) =>
      dest.voyages.some(
        (v) =>
          v.id &&
          scenarioDeltas?.voyageChangeTypes?.[String(v.id)] === 'CREATED'
      )
    );
  }, [isScenarioActive, baseShipGroup, destinations, scenarioDeltas]);

  // Header-level delta flags — prefer value comparison when base data available
  const isEtdChanged = baseShipGroup
    ? shipGroup.etdDate !== baseShipGroup.etdDate
    : hasAnyVoyageDelta(
        destinations,
        ['actualETD', 'initialETD'],
        scenarioDeltas
      );
  const isCarrierChanged = baseShipGroup
    ? String(shipGroup.carrier?.supplierNo ?? '') !==
      String(baseShipGroup.carrier?.supplierNo ?? '')
    : hasAnyVoyageDelta(destinations, ['carrier'], scenarioDeltas);
  const isDepPortChanged = baseShipGroup
    ? (shipGroup.departurePort?.unLocode ?? '') !==
      (baseShipGroup.departurePort?.unLocode ?? '')
    : hasAnyVoyageDelta(destinations, ['departurePort'], scenarioDeltas);
  const isPortOpChanged = baseShipGroup
    ? String(shipGroup.portOperator?.supplierNo ?? '') !==
      String(baseShipGroup.portOperator?.supplierNo ?? '')
    : hasAnyVoyageDelta(destinations, ['portOperator'], scenarioDeltas);
  const isShipChanged = baseShipGroup
    ? (shipGroup.ship?.imo ?? '') !== (baseShipGroup.ship?.imo ?? '')
    : hasAnyVoyageDelta(destinations, ['ship'], scenarioDeltas);

  // Grand totals across all destinations (exclude cancelled voyages)
  const grandTotals = useMemo(() => {
    const destinationShipmentGroups = Object.values(destinations).map((dest) =>
      dest.voyages.filter((v) => !v.cancelled).flatMap((v) => v.shipments ?? [])
    );
    const allShipments = destinationShipmentGroups.flat();
    const hasAnyStock = allShipments.some((s) => s.stock != null);
    const numericFields = [
      'adjustedPORequest',
      'finalAgreement',
      'effectiveShipped',
    ];
    const scenarioSums = {
      ...withVesselCapacity(destinationShipmentGroups),
      adjustedPORequest: calculateSum(allShipments, 'adjustedPORequest'),
      finalAgreement: calculateSum(allShipments, 'finalAgreement'),
      effectiveShipped: calculateSum(allShipments, 'effectiveShipped'),
    };

    // Track which shipment fields have any scenario delta
    const _deltaFields = new Set();

    if (baseShipGroup) {
      // Value comparison: compare aggregated sums against base
      const baseDestinationShipmentGroups = Object.values(
        baseShipGroup.destinations
      ).map((dest) =>
        dest.voyages
          .filter((v) => !v.cancelled)
          .flatMap((v) => v.shipments ?? [])
      );
      const baseShipments = baseDestinationShipmentGroups.flat();
      const baseSums = {
        ...withVesselCapacity(baseDestinationShipmentGroups),
        adjustedPORequest: calculateSum(baseShipments, 'adjustedPORequest'),
        finalAgreement: calculateSum(baseShipments, 'finalAgreement'),
        effectiveShipped: calculateSum(baseShipments, 'effectiveShipped'),
      };
      if (
        scenarioSums.firstShippingLineOffer !==
          baseSums.firstShippingLineOffer ||
        scenarioSums.firstShippingLineOfferError !==
          baseSums.firstShippingLineOfferError
      ) {
        _deltaFields.add('firstShippingLineOffer');
      }
      for (const field of numericFields) {
        if (scenarioSums[field] !== baseSums[field]) _deltaFields.add(field);
      }
    } else {
      // Fallback: delta-key-based detection
      if (
        hasDestShipmentDelta(
          allShipments,
          'firstShippingLineOffer',
          scenarioDeltas
        )
      ) {
        _deltaFields.add('firstShippingLineOffer');
      }
      for (const field of numericFields) {
        if (hasDestShipmentDelta(allShipments, field, scenarioDeltas)) {
          _deltaFields.add(field);
        }
      }
      const anyVoyageCreated = Object.values(destinations).some((dest) =>
        dest.voyages.some(
          (v) =>
            v.id &&
            scenarioDeltas?.voyageChangeTypes?.[String(v.id)] === 'CREATED'
        )
      );
      if (anyVoyageCreated) {
        _deltaFields.add('firstShippingLineOffer');
        for (const field of numericFields) _deltaFields.add(field);
      }
    }

    return {
      ...scenarioSums,
      readyForShipment: calculateSum(allShipments, 'readyForShipment', true),
      totalBlockedCars: calculateSum(allShipments, 'totalBlockedCars', true),
      hasAnyStock,
      _deltaFields,
    };
  }, [destinations, scenarioDeltas, baseShipGroup]);

  const destEntries = Object.entries(destinations).sort(
    ([, a], [, b]) => new Date(a.actualETA ?? 0) - new Date(b.actualETA ?? 0)
  );

  const scenarioCreatedContainerSx = isGroupScenarioCreated
    ? {
        borderRadius: '6px',
        border: (t) => `2px solid ${getScenarioHighlightColor(t)}`,
        backgroundColor: (t) => scenarioCreatedBgColor(t),
      }
    : undefined;

  return (
    <Box sx={scenarioCreatedContainerSx}>
      <CardContainer>
        <Box sx={{ width: '100%' }}>
          {/* ── Ship Header ─────────────────────────────────────── */}
          <Grid container spacing={2} sx={{ alignItems: 'center' }}>
            <Grid xs={8}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mr: 2,
                    minWidth: 80,
                  }}
                >
                  <Typography
                    variant='h3'
                    sx={
                      isEtdChanged
                        ? { color: getScenarioHighlightColor(theme) }
                        : undefined
                    }
                  >
                    {formatDateCompact(etdDate)}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{
                      color: isEtdChanged
                        ? getScenarioHighlightColor(theme)
                        : 'text.secondary',
                    }}
                  >
                    {new Date(etdDate).getFullYear?.() || ''}
                  </Typography>
                </Box>
                <DirectionsBoatOutlined sx={{ fontSize: 28, mr: 1.5 }} />
                <Box>
                  <Typography
                    variant='h3'
                    sx={
                      isShipChanged
                        ? { color: getScenarioHighlightColor(theme) }
                        : undefined
                    }
                  >
                    {ship?.name ?? 'Unknown Ship'}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{
                      color: isCarrierChanged
                        ? getScenarioHighlightColor(theme)
                        : 'text.secondary',
                    }}
                  >
                    {ship?.imo ?? 'N/A'}
                    {carrier?.supplierName ? ` · ${carrier.supplierName}` : ''}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid xs={4}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Chip
                  label={departurePort?.unLocode ?? 'N/A'}
                  variant='filled'
                  color={isDepPortChanged ? 'warning' : 'default'}
                />
                <Tooltip title='Port Operator'>
                  <Chip
                    label={portOperator?.supplierName ?? 'N/A'}
                    variant='outlined'
                    color={isPortOpChanged ? 'warning' : 'default'}
                  />
                </Tooltip>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={
                    isScenarioViewOnly ? (
                      <VisibilityOutlined />
                    ) : (
                      <EditOutlined />
                    )
                  }
                  onClick={() => onEditGroup?.(shipGroup)}
                  sx={{ minWidth: 120 }}
                >
                  {isScenarioViewOnly ? 'View Ship' : 'Edit Ship'}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* ── Route Visualization ─────────────────────────────── */}
          <Box
            sx={{
              mt: 1,
              mx: -1,
              p: 1,
              borderRadius: 1,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(0,0,0,0.02)',
            }}
          >
            <RouteLine
              departurePort={departurePort}
              etdDate={etdDate}
              destinations={destinations}
            />
          </Box>

          {/* ── Grand Total Summary ─────────────────────────────── */}
          <BorderedGrid
            container
            spacing={0.5}
            columns={19}
            sx={{ alignItems: 'center' }}
          >
            {/* Column Headers */}
            <Grid xs={3}>
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                Ship Total
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant='subtitle2'>
                First Shipping Line Offer
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant='subtitle2'>Adjusted PO Request</Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant='subtitle2'>Final Agreement</Typography>
            </Grid>
            <Grid xs={3}>
              <Typography variant='subtitle2'>Effective Shipped</Typography>
            </Grid>
            <Grid
              xs={2}
              paddingLeft={2}
              sx={{ borderLeft: `1px solid ${theme.palette.divider}` }}
            >
              <Typography variant='subtitle2'>Ready For Shipment</Typography>
            </Grid>
            <Grid xs={2}>
              <Typography variant='subtitle2'>Blocked</Typography>
            </Grid>

            {/* Values */}
            <Grid xs={3} />
            <Grid xs={3}>
              <VesselCapacityValue
                value={grandTotals.firstShippingLineOffer}
                isError={grandTotals.firstShippingLineOfferError}
                note={grandTotals.firstShippingLineOfferNote}
                variant='subtitle1'
                highlighted={grandTotals._deltaFields?.has(
                  'firstShippingLineOffer'
                )}
              />
            </Grid>
            <Grid xs={3}>
              <Typography
                variant='subtitle1'
                sx={
                  grandTotals._deltaFields?.has('adjustedPORequest')
                    ? getDeltaSx(theme)
                    : undefined
                }
              >
                {formatNumberDE(grandTotals.adjustedPORequest)}
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography
                variant='subtitle1'
                sx={
                  grandTotals._deltaFields?.has('finalAgreement')
                    ? getDeltaSx(theme)
                    : undefined
                }
              >
                {formatNumberDE(grandTotals.finalAgreement)}
              </Typography>
            </Grid>
            <Grid xs={3}>
              <Typography
                variant='subtitle1'
                sx={
                  grandTotals._deltaFields?.has('effectiveShipped')
                    ? getDeltaSx(theme)
                    : undefined
                }
              >
                {formatNumberDE(grandTotals.effectiveShipped)}
              </Typography>
            </Grid>
            <Grid
              xs={2}
              paddingLeft={2}
              sx={{ borderLeft: `1px solid ${theme.palette.divider}` }}
            >
              <Typography variant='subtitle1'>
                {grandTotals.hasAnyStock
                  ? formatNumberDE(grandTotals.readyForShipment)
                  : 'No Data'}
              </Typography>
            </Grid>
            <Grid xs={2}>
              <Typography variant='subtitle1'>
                {grandTotals.hasAnyStock
                  ? formatNumberDE(grandTotals.totalBlockedCars)
                  : 'No Data'}
              </Typography>
            </Grid>
          </BorderedGrid>

          {/* ── Destination Ports ────────────────────────────────── */}
          <Box sx={{ mt: 2 }}>
            {destEntries.map(([portKey, { port, voyages, actualETA }]) => (
              <DestinationPortSection
                key={portKey}
                destinationPort={port}
                voyages={voyages}
                actualETA={actualETA}
                etdDate={etdDate}
                handleHistoryClick={handleHistoryClick}
                scenarioDeltas={scenarioDeltas}
                baseDest={baseShipGroup?.destinations?.[portKey] ?? null}
              />
            ))}
          </Box>
        </Box>
      </CardContainer>
    </Box>
  );
};

export default ShipCard;
